import { useState, useEffect, useRef, useLayoutEffect } from "react"
import Slider from "react-slick"
import { PinchToZoomImage } from "./pinchToZoomImage"
import { ExtededImageDetails } from "images/interfaces"
import * as apiUrls from "config/apiUrls"
import { ImageSize } from "constants/imageSizes"

interface ArticleImageGalleryMobileProps {
    images: ExtededImageDetails[]
    articleId: string
    articleTitle: string
}

export const ArticleImageGalleryMobile = (props: ArticleImageGalleryMobileProps) => {

    const { images, articleId, articleTitle } = props
    const [isVisible, setIsVisible] = useState<boolean>(false)
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
    const [swipeEnabled, setSwipeEnabled] = useState<boolean>(true)

    const animationFrameRef: any = useRef<number>();
    const hasAnimatedRef: any = useRef<boolean>(false);
    const clickedElementRef: any = useRef<Element | null>(null);
    let numOfImages = images.length ? images.length : 0
    const isImageGotCaption = images && images[currentImageIndex] && images[currentImageIndex].caption
    const isImageGotCredit = images && images[currentImageIndex] && (images[currentImageIndex].credit !== "") && (images[currentImageIndex].credit !== " ")
    const settings = {
        infinite: true,
        dots: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        speed: 500,
        arrows: false,
        rtl: false,
        swipe: swipeEnabled,
        touchMove: swipeEnabled,
        initialSlide: currentImageIndex,
        afterChange: (next: any) => { setCurrentImageIndex(next); setSwipeEnabled(true) },
    }

    useEffect(() => {
        if (typeof window === "undefined") return;

        const elements = document.querySelectorAll(".gelleryOpener");

        const handleClick = (el: Element) => {
            open();

            clickedElementRef.current = el;

            const componentId = el.getAttribute("data-image-id")
            let indexOfMatchingImageComponentId = 0;

            for (let i = 0; i < images.length; i++) {
                if (images[i]) {
                    if (images[i].imageId === componentId) { indexOfMatchingImageComponentId = i }
                }
            }

            setCurrentImageIndex(indexOfMatchingImageComponentId);
            setIsVisible(true);
        }

        for (let el of elements) {
            el.addEventListener("click", () => handleClick(el));
        }

        return () => {
            for (let el of elements) {
                el.removeEventListener("click", () => handleClick(el));
            }
        }
    }, [images])

    useLayoutEffect(() => {
        if (typeof window === "undefined") return;
        if (!isVisible) {
            hasAnimatedRef.current = false;
            return;
        }

        const originElement = clickedElementRef.current;

        if (!originElement) return;

        let clone: HTMLImageElement | null = null;
        let attempts = 0;
        const maxAttempts = 60;

        const findAndAnimate = () => {
            // Find the active slide image. react-slick adds .slick-active to the visible slide.
            const target = document.querySelector('.articleImageGalleryMobile .slick-slide.slick-active img') as HTMLImageElement;

            if (target && target.complete && target.naturalWidth > 0) {
                const targetRect = target.getBoundingClientRect();

                // Ensure the target has a reasonable size
                if (targetRect.width > 50 && targetRect.height > 50) {
                    if (hasAnimatedRef.current) return;
                    hasAnimatedRef.current = true;

                    // Create clone for animation
                    clone = document.createElement("img");
                    clone.src = target.src;
                    clone.style.position = "fixed";
                    clone.style.zIndex = "10000";
                    clone.style.objectFit = "contain";
                    clone.style.transition = "none";
                    clone.style.willChange = "transform";

                    // Match target geometry (fixed position), but correct the horizontal position
                    // because react-slick might report the slide as being off-screen (e.g. in the track flow)
                    // before it snaps to the active slide.
                    const viewportWidth = window.innerWidth;
                    const destWidth = targetRect.width;
                    const destHeight = targetRect.height;
                    const destTop = targetRect.top;
                    // Force the clone to be horizontally centered in the viewport
                    const destLeft = (viewportWidth - destWidth) / 2;

                    clone.style.top = `${destTop}px`;
                    clone.style.left = `${destLeft}px`;
                    clone.style.width = `${destWidth}px`;
                    clone.style.height = `${destHeight}px`;

                    // Calculate transform from origin
                    const originRect = originElement.getBoundingClientRect();

                    const originCenterX = originRect.left + (originRect.width / 2);
                    const originCenterY = originRect.top + (originRect.height / 2);

                    const targetCenterX = destLeft + (destWidth / 2);
                    const targetCenterY = destTop + (destHeight / 2);

                    const translateX = originCenterX - targetCenterX;
                    const translateY = originCenterY - targetCenterY;
                    const scale = originRect.width / destWidth;

                    // Apply start transform (uniform scale to avoid distortion with object-fit: contain)
                    clone.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
                    clone.style.transformOrigin = "center";

                    document.body.appendChild(clone);

                    // Hide real target
                    target.style.opacity = "0";

                    // Animate
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (!clone) return;
                            clone.style.transition = "transform 500ms cubic-bezier(0.2, 0, 0.1, 1)";
                            clone.style.transform = ""; // Reset to target position

                            setTimeout(() => {
                                if (clone) {
                                    clone.remove();
                                    clone = null;
                                }
                                target.style.opacity = "1";
                            }, 500);
                        });
                    });
                    return;
                }
            }

            if (attempts < maxAttempts) {
                attempts++;
                requestAnimationFrame(findAndAnimate);
            }
        };

        findAndAnimate();

        return () => {
            if (clone) {
                clone.remove();
            }
        };

    }, [isVisible]);

    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    const close = (): void => {
        setIsVisible(false)
        analystFunc("Close Gallery", articleId)
    }

    const open = (): void => {
        analystFunc("Open Gallery", articleId)
    }

    const openShare = async () => {
        const shareData = { url: window.location.href, title: articleTitle }
        try {
            // @ts-ignore 
            await navigator.share(shareData)
        } catch (err) {
            console.log("error with sharing function", err);
        }
    }

    if (isVisible) return (
        <div className="articleImageGalleryMobile" onClick={close} id="images_gallery" tabIndex={0} role="dialog" aria-label="images gallery" key={"MobileArticleImagesGallery"}>
            <div className="GalleryWrapper" onClick={e => e.stopPropagation()}>
                <button className="closeBtn" title={__("Close")} aria-label={__("Close")} onClick={close} />

                <div className="imageContainer">
                    <Slider {...settings} >
                        {_.map(images, (image) =>
                            <div className="imageSlide" key={image.imageId}>
                                <PinchToZoomImage onZoomChange={(zoomed) => setSwipeEnabled(!zoomed)}>
                                    <img src={apiUrls.getImageUrl(image, { imageSize: ImageSize.xLarge })} role="image" aria-label={image.alt} />
                                </PinchToZoomImage>
                                
                                <div className="imageInfo">
                                    {isImageGotCaption && <div className="caption">{image.caption}</div>}

                                    {isImageGotCredit && <div className="credit">({image.credit})</div>}
                                </div>
                            </div>
                        )}
                    </Slider>
                </div>

                <div className="bottom-container">
                    {numOfImages > 1 &&
                        <div className="imagesCounter">
                            <span className="current">{currentImageIndex + 1 < 10 ? `${currentImageIndex + 1}` : currentImageIndex + 1}/</span>
                            <span className="totalNumOfImages">{numOfImages < 10 ? `${numOfImages}` : numOfImages}</span>
                        </div>
                    }

                    <button className="shareImageBtn" onClick={openShare}></button>
                </div>
            </div>
        </div>
    )
    else return null
}

ArticleImageGalleryMobile.siteScriptName = "ArticleImageGalleryMobile"

export const analystFunc = (action: string, articleId: string) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
        event: 'GA_Event',
        Category: 'Photo Gallery',
        Action: action,
        Label: articleId
    });
}
